/**
 * Add-on offer (bird screen upsell) for the product page.
 *
 * addonOfferMatch() is a pure function so it can be unit-tested; the <addon-offer>
 * custom element below wires it to Dawn's product form, pub/sub, and cart notification.
 */
function addonOfferMatch(data, parentVariantId) {
  if (parentVariantId === undefined || parentVariantId === null || parentVariantId === '') return [];
  const parent = data.parent.variants.find((v) => String(v.id) === String(parentVariantId));
  if (!parent) return [];
  return data.addons
    .map((addon) => {
      const variant = addon.variants.find((v) => v.available && v.match === parent.match);
      return variant ? { addon, variant } : null;
    })
    .filter(Boolean);
}

window.addonOfferMatch = addonOfferMatch;

if (!customElements.get('addon-offer')) {
  customElements.define(
    'addon-offer',
    class AddonOffer extends HTMLElement {
      connectedCallback() {
        this.data = JSON.parse(this.querySelector('[data-addon-offer-data]').textContent);
        this.sectionId = this.dataset.section;
        this.productForm = document.getElementById(this.dataset.productForm);
        this.variantInput = this.productForm?.querySelector('input[name="id"]');
        this.quantityInput = document.getElementById(`Quantity-${this.sectionId}`);
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.modal = this.dataset.showModal === 'true' ? document.getElementById(`AddonModal-${this.sectionId}`) : null;
        this.pending = false; // true while our modal owns the "item added" moment
        this.suppressReopen = false; // set when the modal closes because the add-on was added
        this.lastSubmission = null; // { variantId, quantity, opener } captured from OUR product form's submit

        // Dawn calls cart.open() from renderContents() right after the nozzle is added. While the
        // owning offer's modal is pending, swallow that open; we reopen it ourselves on dismiss.
        // Wrap once per cart element. Ownership is claimed by the instance that actually opens its
        // modal (not on connect: quick-add renders throwaway instances, and the theme editor
        // re-renders sections) and released when that modal closes or the instance disconnects.
        if (this.cart && !this.cart.addonOfferWrapped) {
          const originalOpen = this.cart.open.bind(this.cart);
          this.cart.open = (...args) => {
            if (!this.cart.addonOfferOwner?.pending) originalOpen(...args);
          };
          this.cart.addonOfferWrapped = true;
        }

        this.querySelectorAll('[data-addon-card]').forEach((card) => this.bindCard(card));
        if (this.modal) {
          this.modal.querySelectorAll('[data-addon-card]').forEach((card) => this.bindCard(card));
          this.modal.querySelector('[data-addon-dismiss]')?.addEventListener('click', () => this.modal.hide());
          this.onModalClosed = this.onModalClosed.bind(this);
          document.body.addEventListener('modalClosed', this.onModalClosed);
        }

        // Record what OUR form submits so the modal reflects the submitted variant/quantity, not the
        // page state when the response lands — and so quick-add submissions elsewhere never qualify.
        this.onProductFormSubmit = () => {
          // Dawn ignores submits while the previous one is in flight (aria-disabled on the button);
          // don't let such a repeat overwrite the snapshot of the request that actually went out.
          const submitButton = this.productForm.querySelector('[type="submit"]');
          if (submitButton?.getAttribute('aria-disabled') === 'true') return;
          this.lastSubmission = {
            variantId: this.variantInput?.value,
            quantity: this.nozzleQuantity,
            opener: document.activeElement,
          };
        };
        this.productForm?.addEventListener('submit', this.onProductFormSubmit, true);

        this.onSync = () => this.sync();
        this.quantityInput?.addEventListener('change', this.onSync);
        // Dawn clears this input (and dispatches change) for unavailable option combinations
        // WITHOUT publishing variantChange, so listen to the input directly too.
        this.variantInput?.addEventListener('change', this.onSync);
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data?.sectionId !== this.sectionId) return;
          this.sync(event.data.variant?.id);
        });
        // Dawn resets the quantity input to the variant's minimum after a cart update.
        this.unsubscribeQuantity = PUB_SUB_EVENTS.quantityUpdate
          ? subscribe(PUB_SUB_EVENTS.quantityUpdate, this.onSync)
          : undefined;
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
        this.unsubscribeCartError = PUB_SUB_EVENTS.cartError
          ? subscribe(PUB_SUB_EVENTS.cartError, (event) => {
              if (event.source === 'product-form') this.lastSubmission = null;
            })
          : undefined;

        this.sync();
      }

      disconnectedCallback() {
        this.unsubscribeVariant?.();
        this.unsubscribeQuantity?.();
        this.unsubscribeCart?.();
        this.unsubscribeCartError?.();
        this.quantityInput?.removeEventListener('change', this.onSync);
        this.variantInput?.removeEventListener('change', this.onSync);
        this.productForm?.removeEventListener('submit', this.onProductFormSubmit, true);
        if (this.modal) document.body.removeEventListener('modalClosed', this.onModalClosed);
        this.releaseCart();
      }

      releaseCart() {
        if (this.cart && this.cart.addonOfferOwner === this) this.cart.addonOfferOwner = null;
      }

      get nozzleQuantity() {
        return Math.max(1, parseInt(this.quantityInput?.value, 10) || 1);
      }

      // Recompute matches for the nozzle's current variant and repaint the inline cards.
      sync(variantId = this.variantInput?.value) {
        const matches = addonOfferMatch(this.data, variantId);
        this.renderCards(this, matches, this.nozzleQuantity);
        this.hidden = matches.length === 0;
      }

      renderCards(root, matches, quantity) {
        root.querySelectorAll('[data-addon-card]').forEach((card) => {
          const match = matches.find((m) => String(m.addon.id) === card.dataset.addonId);
          card.hidden = !match;
          if (!match) return;
          card.querySelector('[data-addon-option]').value = String(match.variant.id);
          card.querySelector('[data-addon-qty]').value = quantity;
          this.updateCardPrice(card);
        });
      }

      updateCardPrice(card) {
        const addon = this.data.addons.find((a) => String(a.id) === card.dataset.addonId);
        const selectedId = card.querySelector('[data-addon-option]').value;
        const variant = addon?.variants.find((v) => String(v.id) === selectedId);
        const price = variant?.price ?? '';
        card.querySelector('[data-addon-price]').textContent = price;
        const buttonPrice = card.querySelector('[data-addon-add-price]');
        if (buttonPrice) buttonPrice.textContent = price;
      }

      bindCard(card) {
        card.querySelector('[data-addon-option]').addEventListener('change', () => this.updateCardPrice(card));
        card.addEventListener('submit', (event) => {
          event.preventDefault();
          this.addToCart(card);
        });
      }

      async addToCart(card) {
        const button = card.querySelector('[data-addon-add]');
        const spinner = button.querySelector('.loading__spinner');
        const error = card.querySelector('[data-addon-error]');
        if (button.getAttribute('aria-disabled') === 'true') return;

        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        spinner?.classList.remove('hidden');
        error.hidden = true;

        const inModal = card.dataset.context === 'modal' && !!this.modal;
        // Where focus returns when the notification closes: the inline Add button, or — for the
        // modal path, whose button is about to disappear — the Add to cart button that opened it.
        const focusTarget = inModal ? this.modalOpener || document.activeElement : document.activeElement;

        const formData = new FormData(card); // id (select) + quantity
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement?.(focusTarget);
        }
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        try {
          const response = await fetch(window.routes.cart_add_url, config).then((r) => r.json());
          if (response.status) {
            error.textContent = response.description || response.message || window.cartStrings?.error;
            error.hidden = false;
            return;
          }
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'addon-offer',
            productVariantId: formData.get('id'),
            cartData: response,
          });
          if (inModal) {
            this.pending = false;
            this.suppressReopen = true;
            this.modal.hide();
          }
          if (this.cart) {
            this.cart.classList.remove('is-empty');
            this.cart.renderContents(response);
          } else {
            window.location = window.routes.cart_url;
          }
        } catch (e) {
          console.error(e);
          error.textContent = window.cartStrings?.error || 'Something went wrong.';
          error.hidden = false;
        } finally {
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          spinner?.classList.add('hidden');
        }
      }

      // Dawn's product-form publishes this synchronously BEFORE it calls cart.renderContents(),
      // so setting `pending` here is what keeps the notification from opening.
      onCartUpdate(event) {
        if (!this.modal || event.source !== 'product-form') return;
        const submission = this.lastSubmission;
        if (!submission || String(submission.variantId) !== String(event.productVariantId)) return;
        this.lastSubmission = null;
        const matches = addonOfferMatch(this.data, submission.variantId);
        if (!matches.length) return;

        const added = event.cartData || {};
        const eyebrow = this.modal.querySelector('[data-addon-added]');
        if (eyebrow) {
          // Variant title only ("3\" / UV+ / Black"): the product name is already on the page.
          const label = added.variant_title || added.product_title || '';
          eyebrow.textContent = label ? `Added to cart · ${label}` : '';
        }
        this.renderCards(this.modal, matches, submission.quantity);
        if (this.cart) this.cart.addonOfferOwner = this;
        this.pending = true;
        this.suppressReopen = false;
        this.modalOpener = submission.opener || document.activeElement;
        this.modal.show(this.modalOpener);
      }

      // Dawn dispatches `modalClosed` on body before removing the modal's `open` attribute,
      // so `hasAttribute('open')` is true only when it is OUR modal that's closing.
      onModalClosed() {
        if (!this.modal.hasAttribute('open')) return;
        const wasPending = this.pending;
        this.pending = false;
        this.releaseCart();
        if (this.suppressReopen) {
          this.suppressReopen = false;
          return;
        }
        // Defer: the dismissing click is still bubbling, and cart.open() installs a body click
        // listener that would otherwise see that same click and close the notification at once.
        if (wasPending) setTimeout(() => this.cart?.open(), 0);
      }
    },
  );
}
